<script setup lang="ts">
import type { Product } from "~~/shared/types/store";

const props = withDefaults(
  defineProps<{
    title: string;
    products: Product[];
    isLoading?: boolean;
    itemListId?: string;
    itemListName?: string;
  }>(),
  {
    isLoading: false,
    itemListId: undefined,
    itemListName: undefined,
  },
);

const _listId = computed(() => props.itemListId ?? props.title);
const _listName = computed(() => props.itemListName ?? props.title);

// Siatka 2 rzędy × 4 produkty = max 8 pozycji.
const visibleProducts = computed(() => props.products.slice(0, 8));
</script>

<template>
  <section
    v-if="props.isLoading || props.products.length"
    class="product-editorial-section container product-editorial-section--spaced"
  >
    <h2 class="product-editorial-section__title">{{ title }}</h2>

    <div class="product-editorial-section__grid">
      <template v-if="props.isLoading">
        <div
          v-for="n in 8"
          :key="`skeleton-${n}`"
          class="product-editorial-section__item product-editorial-section__skeleton-card"
          aria-hidden="true"
        >
          <div
            class="product-editorial-section__skeleton-image product-editorial-section__skeleton-image--portrait"
          />
        </div>
      </template>

      <template v-else>
        <div
          v-for="(product, index) in visibleProducts"
          :key="product.id"
          class="product-editorial-section__item"
        >
          <ProductCard
            :product="product"
            :item-list-id="_listId"
            :item-list-name="_listName"
            :index="index"
          />
        </div>
      </template>
    </div>
  </section>
</template>

<style scoped>
.product-editorial-section {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.product-editorial-section--spaced {
  margin-top: 100px;
}

.product-editorial-section__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  line-height: 1;
}

/* ── Siatka: 2 rzędy × 4 (desktop / mini-desktop) ── */
.product-editorial-section__grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 4px;
}

.product-editorial-section__item {
  min-width: 0;
}

/* Karty produktu zachowują proporcję zdjęcia z ProductCard (token marki
   --product-card-img-ratio: odzież 0.75, proles 1). */

/* ── Skeleton ── */
.product-editorial-section__skeleton-card {
  position: relative;
}

.product-editorial-section__skeleton-image {
  width: 100%;
  background: var(--color-border-md);
  animation: product-editorial-pulse 1.5s ease-in-out infinite;
}

.product-editorial-section__skeleton-image--portrait {
  aspect-ratio: var(--product-card-img-ratio);
}

@keyframes product-editorial-pulse {
  0% {
    opacity: 1;
  }

  50% {
    opacity: 0.4;
  }

  100% {
    opacity: 1;
  }
}

/* ── Responsywność ── */

/* Tablet: 3 kolumny */
@media (max-width: 959px) {
  .product-editorial-section {
    margin-top: 50px;
  }

  .product-editorial-section__grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* Mobile: 2 kolumny */
@media (max-width: 599px) {
  .product-editorial-section__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Od 1200px pasek kafla u proles stoi pod zdjęciem — szkielet rezerwuje
   jego wysokość jak szkielet ProductCard (token marki, odzież 0). */
@media (min-width: 1200px) {
  .product-editorial-section__skeleton-image--portrait {
    margin-bottom: var(--product-card-bar-reserve);
  }
}
</style>
